import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import creditApi from "./creditApi";
import type {
  AdminAddTeamMemberRequest,
  AdminAuditEntry,
  AdminCreditLog,
  AdminCreditRequest,
  AdminDebitRequest,
  AdminRemoveTeamMemberRequest,
  AdminReverseRequest,
  AdminTeamOverview,
  AdminTeamSummary,
  AdminUser,
  AdminUserOverview,
  AdminUserSearchQuery,
  BillingSettings,
  DebitQueueEntry,
  Discount,
  Feature,
  FundTeamPoolRequest,
  FundTeamPoolResult,
  AuditQuery,
  LedgerQuery,
  Plan,
  PlanImpact,
  Provider,
  ResolvedEntitlements,
  SaveDiscountRequest,
  SavePlanRequest,
  SaveProviderRequest,
  ServiceConfig,
  SetPlanCreditRequest,
  SetPlanEntitlementRequest,
  SetUserConfigRequest,
  SetUserStatusRequest,
  SetUserStatusResult,
  SubscribeRequest,
  TeamTransferRequest,
  UpdateBillingSettingsRequest,
  UpsertFeatureRequest,
} from "@/lib/types/admin.types";
import type { AllocationResult, ReclaimResult, TeamPoolLog, AddMemberResult } from "@/lib/types/team.types";

/**
 * The internal super-admin console's API.
 *
 * Talks to the Express middleware at `/api/admin/*`, which is gated on
 * `requireSuperAdmin` and answers 404 — not 403 — to everybody else. So every hook
 * here fails with a 404 for a normal user even if they somehow reach the screen; the
 * client-side guard exists to keep that from happening, not to be the thing that
 * stops it.
 *
 * Routed through `axiosBaseQuery` for the same reason teamApi is: these calls need
 * the session handling in lib/axios, so an expired access token is refreshed and
 * retried rather than surfacing as a spurious failure mid-edit.
 *
 * TAGS, AND WHY THE PLAN ONES REACH SO WIDE. Editing a tier does not only change the
 * tier: upstream it writes through to the stored configuration of every subscriber.
 * So a plan mutation has to invalidate `AdminUser` as well as `AdminPlan`, or an open
 * user detail screen keeps showing the entitlements from before the edit — which
 * reads as the edit having silently not applied.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

function unwrap<T>(fallback: T) {
  return (response: Envelope<T> | undefined) => response?.data ?? fallback;
}

/**
 * How long to wait before re-reading an account a QUEUED debit has just been
 * filed against.
 *
 * A debit does not apply inline. It goes on the credit service's `debit_queue`
 * (see admin.controller's debitUser -> /api/v1/debit-queue/debit), and a worker
 * there drains it on a timer — `DEBIT_WORKER_INTERVAL_MS`, 2s by default. So
 * every cache this touches is invalidated twice: once the moment the POST
 * resolves, and once after the queue has had time to drain. Invalidating only
 * on resolve is worse than not invalidating at all — RTK Query refetches once
 * per invalidation, so that single refetch reads the PRE-debit balance and then
 * sits on it as fresh data, which is exactly the "I took credit and nothing
 * moved" the screens were showing.
 *
 * Generous rather than exact, because the interval is configurable upstream and
 * an entry queued behind a concurrent spend waits its turn.
 */
const DEBIT_QUEUE_SETTLE_MS = 4_000;

type OwnCreditLifecycle = {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
  queryFulfilled: Promise<unknown>;
};

/** The signed-in user's id, for telling "I edited myself" from "I edited them". */
function signedInUserId(getState: () => unknown): string | undefined {
  return (getState() as { auth?: { currentUser?: { _id?: string } | null } })?.auth
    ?.currentUser?._id;
}

/**
 * Refreshes the acting admin's OWN balance and ledger after an admin mutation
 * that moved credit on their account.
 *
 * `creditApi` is a separate `createApi` instance, so nothing invalidated here
 * reaches it on its own — the top-bar balance badge (breadcrumbs.tsx) and
 * /settings/usage both read from it and would otherwise keep serving the
 * pre-change figure until a full reload, since the badge is mounted app-wide
 * (so `keepUnusedDataFor` never expires it) and neither api refetches on focus.
 * Same cross-slice hand-off teamApi's `refreshOwnCreditCache` already makes.
 *
 * Only when the target IS the signed-in user: an admin topping up somebody else
 * has not changed their own balance, and refetching it would be two requests
 * that can only return what is already on screen.
 */
async function refreshOwnCreditCache(
  arg: { userId?: string } | undefined,
  { dispatch, getState, queryFulfilled }: OwnCreditLifecycle
) {
  try {
    await queryFulfilled;
  } catch {
    // The mutation failed, so no balance changed — nothing to refresh.
    return;
  }
  if (!arg?.userId || arg.userId !== signedInUserId(getState)) return;
  dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
}

/**
 * Same as above for the debit path, which lands on a queue rather than applying
 * inline — see DEBIT_QUEUE_SETTLE_MS. The admin console's own view of the
 * account is re-read on the same delay for the same reason: `invalidatesTags`
 * fires the instant the POST resolves, which for a queued debit is before the
 * number it is re-reading has moved.
 */
async function refreshCachesAfterQueuedDebit(
  arg: { userId?: string } | undefined,
  { dispatch, getState, queryFulfilled }: OwnCreditLifecycle
) {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  const isSelf = !!arg?.userId && arg.userId === signedInUserId(getState);
  if (isSelf) {
    dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
  }
  setTimeout(() => {
    if (isSelf) {
      dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
    }
    if (arg?.userId) {
      dispatch(adminApi.util.invalidateTags([{ type: "AdminUser", id: arg.userId }, "AdminQueue"]));
    }
  }, DEBIT_QUEUE_SETTLE_MS);
}

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: axiosBaseQuery("/api/admin"),
  tagTypes: [
    "AdminPlan",
    "AdminFeature",
    "AdminServiceConfig",
    "AdminBillingSettings",
    "AdminDiscount",
    "AdminUser",
    "AdminQueue",
    "AdminTeam",
    "AdminProvider",
  ],
  // Deliberately NOT refetchOnFocus. These are editing screens with half-filled
  // forms in them, and a refetch on every window focus is how an in-progress edit
  // gets overwritten by a tab switch.
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    // --- Plans -------------------------------------------------------------

    getPlans: builder.query<Plan[], void>({
      query: () => ({ url: "/plans", params: { page: 1, limit: 100 } }),
      transformResponse: unwrap<Plan[]>([]),
      providesTags: ["AdminPlan"],
    }),

    savePlan: builder.mutation<Plan, SavePlanRequest>({
      // One endpoint for both, chosen by whether we already have an id: the upstream
      // API splits create (POST) from update (PUT) but every field is the same, and
      // making the caller pick would put that decision in each form.
      query: (body) => ({ url: "/plans", method: body.id ? "PUT" : "POST", body }),
      transformResponse: (response: Envelope<Plan>) => response.data,
      invalidatesTags: ["AdminPlan", "AdminUser"],
    }),

    deletePlan: builder.mutation<null, { id: string }>({
      query: (body) => ({ url: "/plans", method: "DELETE", body }),
      transformResponse: unwrap<null>(null),
      invalidatesTags: ["AdminPlan", "AdminUser"],
    }),

    /**
     * Changes ONE feature on ONE tier. This is what the entitlement matrix saves
     * through, cell by cell, rather than PUTting the whole plan — two admins editing
     * different cells would otherwise overwrite each other's work.
     *
     * Passing `entitlement: null` removes the key, which DENIES the feature.
     */
    setPlanEntitlement: builder.mutation<Plan, SetPlanEntitlementRequest>({
      query: (body) => ({ url: "/plans/entitlement", method: "PUT", body }),
      transformResponse: (response: Envelope<Plan>) => response.data,
      invalidatesTags: ["AdminPlan", "AdminUser"],
    }),

    /**
     * The credit half of the same operation: what one service type costs on this tier.
     *
     * `credit: null` removes the key, and the tier's subscribers then fall back to the
     * global service_configs default — which is a price, NOT free.
     */
    setPlanCredit: builder.mutation<Plan, SetPlanCreditRequest>({
      query: (body) => ({ url: "/plans/credit", method: "PUT", body }),
      transformResponse: (response: Envelope<Plan>) => response.data,
      invalidatesTags: ["AdminPlan", "AdminUser"],
    }),

    /**
     * Puts one account on a tier, copying that tier's entitlements and prices onto
     * their own configuration document.
     *
     * Idempotent and safe to repeat on purpose: re-subscribing refreshes the
     * plan-sourced values and leaves hand-set ones alone, which is how an account
     * that has drifted from its tier is repaired.
     */
    subscribeUser: builder.mutation<unknown, SubscribeRequest>({
      query: (body) => ({ url: "/plans/subscribe", method: "POST", body }),
      invalidatesTags: ["AdminUser"],
      // A subscription grants the tier's credit allowance, so the balance moves.
      onQueryStarted: refreshOwnCreditCache,
    }),

    // --- Feature catalogue -------------------------------------------------

    getFeatures: builder.query<Feature[], void>({
      query: () => "/features",
      transformResponse: unwrap<Feature[]>([]),
      providesTags: ["AdminFeature"],
    }),

    /** Upsert on `key`: omitted fields keep their current value on an existing row. */
    upsertFeature: builder.mutation<Feature, UpsertFeatureRequest>({
      query: (body) => ({ url: "/features", method: "POST", body }),
      transformResponse: (response: Envelope<Feature>) => response.data,
      invalidatesTags: ["AdminFeature", "AdminPlan", "AdminUser"],
    }),

    deleteFeature: builder.mutation<null, { key: string }>({
      query: (body) => ({ url: "/features", method: "DELETE", body }),
      transformResponse: unwrap<null>(null),
      invalidatesTags: ["AdminFeature", "AdminPlan", "AdminUser"],
    }),

    // --- Global credit prices ---------------------------------------------

    getServiceConfigs: builder.query<ServiceConfig[], void>({
      query: () => ({ url: "/service-configs", params: { page: 1, limit: 100 } }),
      transformResponse: unwrap<ServiceConfig[]>([]),
      providesTags: ["AdminServiceConfig"],
    }),

    saveServiceConfig: builder.mutation<
      ServiceConfig,
      { id?: string; type?: string; amount: number; note?: string; featureKey?: string }
    >({
      query: (body) => ({
        url: "/service-configs",
        method: body.id ? "PUT" : "POST",
        body,
      }),
      transformResponse: (response: Envelope<ServiceConfig>) => response.data,
      invalidatesTags: ["AdminServiceConfig", "AdminUser"],
    }),

    deleteServiceConfig: builder.mutation<null, { id: string }>({
      query: (body) => ({ url: "/service-configs", method: "DELETE", body }),
      transformResponse: unwrap<null>(null),
      invalidatesTags: ["AdminServiceConfig", "AdminUser"],
    }),

    // --- Billing settings ----------------------------------------------------
    // The one singleton behind the pricing page's credits-calculator rate.

    getBillingSettings: builder.query<BillingSettings | null, void>({
      query: () => "/billing-settings",
      transformResponse: unwrap<BillingSettings | null>(null),
      providesTags: ["AdminBillingSettings"],
    }),

    updateBillingSettings: builder.mutation<BillingSettings, UpdateBillingSettingsRequest>({
      query: (body) => ({ url: "/billing-settings", method: "PUT", body }),
      transformResponse: (response: Envelope<BillingSettings>) => response.data,
      invalidatesTags: ["AdminBillingSettings"],
    }),

    // --- Discounts -----------------------------------------------------------
    // The catalogue-wide default, one per plan, or a negotiated rate for one
    // account. Invalidates AdminPlan too, since the pricing page's discount
    // numbers are resolved onto GET /plans/pricing rather than stored there.

    getDiscounts: builder.query<Discount[], void>({
      query: () => "/discounts",
      transformResponse: unwrap<Discount[]>([]),
      providesTags: ["AdminDiscount"],
    }),

    saveDiscount: builder.mutation<Discount, SaveDiscountRequest>({
      query: (body) => ({ url: "/discounts", method: body.id ? "PUT" : "POST", body }),
      transformResponse: (response: Envelope<Discount>) => response.data,
      invalidatesTags: ["AdminDiscount", "AdminPlan"],
    }),

    deleteDiscount: builder.mutation<null, { id: string }>({
      query: (body) => ({ url: "/discounts", method: "DELETE", body }),
      transformResponse: unwrap<null>(null),
      invalidatesTags: ["AdminDiscount", "AdminPlan"],
    }),

    // --- Users -------------------------------------------------------------

    searchUsers: builder.query<AdminUser[], AdminUserSearchQuery | void>({
      query: (args) => {
        const { q, status, planKey, page = 1, limit = 20 } = args ?? {};
        return {
          url: "/users",
          params: {
            page,
            limit,
            ...(q ? { q } : {}),
            ...(status ? { status } : {}),
            ...(planKey ? { planKey } : {}),
          },
        };
      },
      transformResponse: unwrap<AdminUser[]>([]),
      providesTags: ["AdminUser"],
    }),

    /** Everything about one account in one request — see AdminUserOverview. */
    getUserOverview: builder.query<AdminUserOverview | null, string>({
      query: (userId) => `/users/${userId}`,
      transformResponse: unwrap<AdminUserOverview | null>(null),
      providesTags: (_result, _error, userId) => [{ type: "AdminUser", id: userId }, "AdminUser"],
    }),

    getUserEntitlements: builder.query<ResolvedEntitlements | null, string>({
      query: (userId) => `/users/${userId}/entitlements`,
      transformResponse: unwrap<ResolvedEntitlements | null>(null),
      providesTags: (_result, _error, userId) => [{ type: "AdminUser", id: userId }],
    }),

    getUserLogs: builder.query<
      AdminUser[] | unknown[],
      { userId: string; page?: number; limit?: number; direction?: string; status?: string }
    >({
      query: ({ userId, page = 1, limit = 10, direction, status }) => ({
        url: `/users/${userId}/logs`,
        params: {
          page,
          limit,
          ...(direction ? { direction } : {}),
          ...(status ? { status } : {}),
        },
      }),
      transformResponse: unwrap<unknown[]>([]),
      providesTags: (_result, _error, arg) => [{ type: "AdminUser", id: arg.userId }],
    }),

    /** Mints credit onto one account. The one operation with no counterparty. */
    creditUser: builder.mutation<unknown, AdminCreditRequest>({
      query: (body) => ({ url: "/users/credit", method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }, "AdminQueue"],
      // Applied inline upstream, so one refresh on resolve is enough.
      onQueryStarted: refreshOwnCreditCache,
    }),

    /** Queues a debit, on the same path every real spend takes. */
    debitUser: builder.mutation<unknown, AdminDebitRequest>({
      query: (body) => ({ url: "/users/debit", method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }, "AdminQueue"],
      // Queued, not inline — re-read again once the worker has drained it.
      onQueryStarted: refreshCachesAfterQueuedDebit,
    }),

    /**
     * Sets whether an account may sign in. The BFF decides which of two different
     * upstream authorities to write through — team membership, or the teamless
     * override — see SetUserStatusRequest. May reject with a 409 carrying
     * `details.code === "CONFIRM_RECLAIM_REMAINING"` when disabling a team member who
     * still holds credit; retry with `reclaimRemaining` set once the caller answers.
     */
    setUserStatus: builder.mutation<SetUserStatusResult, SetUserStatusRequest>({
      query: (body) => ({ url: "/users/status", method: "POST", body }),
      transformResponse: (response: Envelope<SetUserStatusResult>) => response.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }],
    }),

    /** Undoes one ledger entry. Amount is derived upstream, never sent. */
    reverseTransaction: builder.mutation<unknown, AdminReverseRequest & { userId: string }>({
      // `userId` is only for the cache tag below — it is stripped from the request
      // body, since the upstream reverse endpoint takes just the transactionId.
      query: ({ userId, ...body }) => ({ url: "/users/reverse", method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }],
      // A reversal moves the balance as surely as the entry it undoes does.
      onQueryStarted: refreshOwnCreditCache,
    }),

    /**
     * Hand-sets any mix of feature ceilings and credit prices for one person. Every
     * value written is marked "manual" upstream and survives later plan edits; a
     * `null` reverts that one key to the plan's value.
     */
    setUserConfig: builder.mutation<unknown, SetUserConfigRequest>({
      query: (body) => ({ url: "/user-configs", method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }],
    }),

    /** Removes the configuration entirely, which is DENIAL, not a reset to defaults. */
    deleteUserConfig: builder.mutation<unknown, { userId: string }>({
      query: (body) => ({ url: "/user-configs", method: "DELETE", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminUser", id: arg.userId }],
    }),

    // --- Debit queue -------------------------------------------------------

    getDebitQueue: builder.query<DebitQueueEntry[], { status?: string } | void>({
      query: (args) => ({
        url: "/debit-queue",
        params: { page: 1, limit: 20, ...(args?.status ? { status: args.status } : {}) },
      }),
      transformResponse: unwrap<DebitQueueEntry[]>([]),
      providesTags: ["AdminQueue"],
    }),

    processDebitQueue: builder.mutation<unknown, { count?: number } | void>({
      query: (body) => ({ url: "/debit-queue/process", method: "POST", body: body ?? {} }),
      invalidatesTags: ["AdminQueue", "AdminUser"],
      // Drains whatever is queued, which may include a debit against the acting
      // admin's own account — there is no userId to compare here, so this one
      // refreshes unconditionally.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
      },
    }),

    // --- Teams ---------------------------------------------------------------

    getTeams: builder.query<AdminTeamSummary[], { page?: number; limit?: number } | void>({
      query: (args) => {
        const { page = 1, limit = 20 } = args ?? {};
        return { url: "/teams", params: { page, limit } };
      },
      transformResponse: unwrap<AdminTeamSummary[]>([]),
      providesTags: ["AdminTeam"],
    }),

    /** Everything about one team in one call — the row, its owner, pool, members, ledger. */
    getTeamOverview: builder.query<AdminTeamOverview | null, string>({
      query: (teamId) => `/teams/${teamId}`,
      transformResponse: unwrap<AdminTeamOverview | null>(null),
      providesTags: (_result, _error, teamId) => [{ type: "AdminTeam", id: teamId }, "AdminTeam"],
    }),

    /** Mints credit into a team's pool. */
    fundTeamPool: builder.mutation<FundTeamPoolResult, FundTeamPoolRequest>({
      query: (body) => ({ url: "/teams/fund", method: "POST", body }),
      transformResponse: (response: Envelope<FundTeamPoolResult>) => response.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminTeam", id: arg.teamId }],
    }),

    /** Moves credit from the pool into one member's own balance. */
    allocateTeamCredit: builder.mutation<AllocationResult, TeamTransferRequest>({
      query: (body) => ({ url: "/teams/allocate", method: "POST", body }),
      // Moves credit onto one member's account — theirs, or the admin's own.
      onQueryStarted: refreshOwnCreditCache,
      transformResponse: (response: Envelope<AllocationResult>) => response.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminTeam", id: arg.teamId }, "AdminUser"],
    }),

    /** Pulls credit from a member's own balance back into the pool. */
    reclaimTeamCredit: builder.mutation<ReclaimResult, TeamTransferRequest>({
      query: (body) => ({ url: "/teams/reclaim", method: "POST", body }),
      // The other direction of the same movement — see allocate above.
      onQueryStarted: refreshOwnCreditCache,
      transformResponse: (response: Envelope<ReclaimResult>) => response.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminTeam", id: arg.teamId }, "AdminUser"],
    }),

    /**
     * Adds a teammate by email, acting as the team's owner. Errors carry the same
     * `details.code` the customer-facing flow uses (CONFIRM_EXISTING_USER,
     * INVITE_NEW, …) — read them with readTeamError, same as everywhere else.
     */
    addTeamMember: builder.mutation<AddMemberResult, AdminAddTeamMemberRequest>({
      query: (body) => ({ url: "/teams/members", method: "POST", body }),
      transformResponse: (response: Envelope<AddMemberResult>) => response.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminTeam", id: arg.teamId }],
    }),

    removeTeamMember: builder.mutation<unknown, AdminRemoveTeamMemberRequest>({
      query: (body) => ({ url: "/teams/members", method: "DELETE", body }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminTeam", id: arg.teamId }],
    }),

    // --- Ledger (global) -----------------------------------------------------

    /** The full ledger, or one filtered slice of it — no userId means every account. */
    getLedger: builder.query<AdminCreditLog[], LedgerQuery | void>({
      query: (args) => {
        const { page = 1, limit = 20, userId, status, direction, category } = args ?? {};
        return {
          url: "/ledger",
          params: {
            page,
            limit,
            ...(userId ? { userId } : {}),
            ...(status ? { status } : {}),
            ...(direction ? { direction } : {}),
            ...(category ? { category } : {}),
          },
        };
      },
      transformResponse: unwrap<AdminCreditLog[]>([]),
    }),

    // --- Needs review ----------------------------------------------------------

    /** Team-transfer intents the worker flagged as unsettleable on its own. */
    getNeedsReview: builder.query<TeamPoolLog[], void>({
      query: () => ({ url: "/teams/needs-review", params: { page: 1, limit: 20 } }),
      transformResponse: unwrap<TeamPoolLog[]>([]),
      providesTags: ["AdminQueue"],
    }),

    /**
     * The bulk-migration dry run for one matrix cell: how many accounts an
     * entitlement or credit-price edit would actually reach before it is saved.
     * Pass exactly one of featureKey/type, matching whichever matrix cell is open.
     */
    getPlanImpact: builder.query<PlanImpact, { planId: string; featureKey?: string; type?: string }>({
      query: ({ planId, featureKey, type }) => ({
        url: `/plans/${planId}/impact`,
        params: { ...(featureKey ? { featureKey } : {}), ...(type ? { type } : {}) },
      }),
      transformResponse: (response: Envelope<PlanImpact>) => response.data,
    }),

    // --- Audit trail -----------------------------------------------------------

    /** Every mutating call this console has made — see AdminAuditEntry. */
    getAudit: builder.query<AdminAuditEntry[], AuditQuery | void>({
      query: (args) => {
        const { page = 1, limit = 25, actorEmail } = args ?? {};
        return {
          url: "/audit",
          params: { page, limit, ...(actorEmail ? { actorEmail } : {}) },
        };
      },
      transformResponse: unwrap<AdminAuditEntry[]>([]),
    }),

    // --- Providers (Workflow service) ---------------------------------------

    getProviders: builder.query<Provider[], void>({
      query: () => "/providers",
      transformResponse: unwrap<Provider[]>([]),
      providesTags: ["AdminProvider"],
    }),

    /** One endpoint for both, chosen by whether we already have an id — same convention as savePlan/saveDiscount. */
    saveProvider: builder.mutation<Provider, SaveProviderRequest>({
      query: (body) => ({ url: "/providers", method: body.id ? "PUT" : "POST", body }),
      transformResponse: (response: Envelope<Provider>) => response.data,
      invalidatesTags: ["AdminProvider"],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useSavePlanMutation,
  useDeletePlanMutation,
  useSetPlanEntitlementMutation,
  useSetPlanCreditMutation,
  useSubscribeUserMutation,
  useGetFeaturesQuery,
  useUpsertFeatureMutation,
  useDeleteFeatureMutation,
  useGetServiceConfigsQuery,
  useSaveServiceConfigMutation,
  useDeleteServiceConfigMutation,
  useGetBillingSettingsQuery,
  useUpdateBillingSettingsMutation,
  useGetDiscountsQuery,
  useSaveDiscountMutation,
  useDeleteDiscountMutation,
  useSearchUsersQuery,
  useGetUserOverviewQuery,
  useGetUserEntitlementsQuery,
  useGetUserLogsQuery,
  useCreditUserMutation,
  useDebitUserMutation,
  useSetUserStatusMutation,
  useReverseTransactionMutation,
  useSetUserConfigMutation,
  useDeleteUserConfigMutation,
  useGetDebitQueueQuery,
  useProcessDebitQueueMutation,
  useGetTeamsQuery,
  useGetTeamOverviewQuery,
  useFundTeamPoolMutation,
  useAllocateTeamCreditMutation,
  useReclaimTeamCreditMutation,
  useAddTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetLedgerQuery,
  useGetNeedsReviewQuery,
  useGetPlanImpactQuery,
  useGetAuditQuery,
  useGetProvidersQuery,
  useSaveProviderMutation,
} = adminApi;
